import { memo, type ChangeEvent } from "react";
import { DateTime } from "luxon";
import { dollars } from "byzantine";
import {
  FREQUENCIES,
  getTotalCountFromRecurrenceString,
} from "byzantine/src/Recurrence";
import filters from "byzantine/src/filters";
import { useLocalization } from "@fluent/react";
import {
  ContentCard,
  DateInput,
  Radio,
  Row,
  TextInput,
} from "@narmi/design_system";
import { Controller } from "react-hook-form";

import type { InternationalWireForm } from "byzantine/src/dbbl/businessLogic/modules/internationalWires";
import type { UseFormSetValue } from "react-hook-form";

import styles from "./InternationalWire.module.scss";

type SetFormValue = UseFormSetValue<InternationalWireForm>;

const DEFAULT_RECURRENCE_COUNT = "12";

export const formatInternationalWireStartDate = (value: string): string => {
  const date = DateTime.fromFormat(value, "M/d/yyyy");
  return date.isValid ? date.toFormat("MM/dd/yyyy") : "";
};

const getEarliestRecurringStartDate = (value?: string): DateTime => {
  const parsedDate = value ? DateTime.fromFormat(value, "M/d/yyyy") : null;
  const tomorrow = DateTime.now().plus({ days: 1 }).startOf("day");

  if (!parsedDate?.isValid || parsedDate < tomorrow) {
    return tomorrow;
  }

  return parsedDate;
};

const getDefaultRecurringUntilDate = (startDate?: string): string =>
  getEarliestRecurringStartDate(startDate)
    .plus({ years: 1 })
    .toFormat("MM/dd/yyyy");

export const getInternationalWireTotalPayments = (
  frequency: string | undefined,
  startDate: string | undefined,
  ends: string | undefined,
  untilDate: string | undefined,
  count: string | undefined
): number => {
  if (!frequency || !startDate || (ends !== "on" && ends !== "after")) {
    return 0;
  }

  const formattedStartDate = formatInternationalWireStartDate(startDate);
  const formattedUntilDate = formatInternationalWireStartDate(untilDate || "");
  if (
    !formattedStartDate ||
    (ends === "on" && !formattedUntilDate) ||
    // Without a count, an "after" rule has no end and RRule.all() never returns.
    (ends === "after" && !count)
  ) {
    return 0;
  }

  return getTotalCountFromRecurrenceString(
    frequency,
    DateTime.fromFormat(formattedStartDate, "MM/dd/yyyy").toFormat(
      "yyyy-MM-dd"
    ),
    ends === "on"
      ? DateTime.fromFormat(formattedUntilDate, "MM/dd/yyyy").toFormat(
          "yyyy-MM-dd"
        )
      : null,
    ends === "after" ? count : null
  );
};

export const isFutureInternationalWireStartDate = (value?: string): boolean => {
  if (!value) return false;
  const date = DateTime.fromFormat(value, "M/d/yyyy");
  return date.isValid && date > DateTime.now().startOf("day");
};

export const handleInternationalWireStartDateChange = (
  value: string,
  onChange: (value: string) => void,
  setValue: SetFormValue
): void => {
  const formattedDate = formatInternationalWireStartDate(value);
  onChange(formattedDate);

  if (isFutureInternationalWireStartDate(formattedDate)) return;

  setValue("recurringRule.frequency", FREQUENCIES.ONCE);
  setValue("recurringRule.isVisible", false);
  setValue("recurringRule.ends", "");
  setValue("recurringRule.untilDate", "");
  setValue("recurringRule.count", "");
};

export const handleInternationalWireFrequencyChange = (
  frequency: string,
  startDate: string | undefined,
  onChange: (value: string) => void,
  setValue: SetFormValue
): void => {
  onChange(frequency);
  setValue("recurringRule.isVisible", frequency !== FREQUENCIES.ONCE);

  if (frequency === FREQUENCIES.ONCE) return;

  if (!isFutureInternationalWireStartDate(startDate)) {
    setValue(
      "startDate",
      DateTime.now().plus({ days: 1 }).toFormat("MM/dd/yyyy")
    );
  }
  setValue("recurringRule.ends", "never");
};

export const handleInternationalWireRecurrenceEndChange = (
  ends: string,
  startDate: string | undefined,
  count: string | undefined,
  untilDate: string | undefined,
  setValue: SetFormValue
): void => {
  setValue("recurringRule.ends", ends);

  if (ends === "on" && !formatInternationalWireStartDate(untilDate || "")) {
    setValue(
      "recurringRule.untilDate",
      getDefaultRecurringUntilDate(startDate)
    );
  }

  if (
    ends === "after" &&
    (!count || !Number.isInteger(Number(count)) || Number(count) <= 0)
  ) {
    setValue("recurringRule.count", DEFAULT_RECURRENCE_COUNT);
  }
};

type InternationalWireStartDateFieldProps = {
  control: any;
  label: string;
  setValue: SetFormValue;
};

export const InternationalWireStartDateField = memo(
  ({ control, label, setValue }: InternationalWireStartDateFieldProps) => (
    <Controller
      name="startDate"
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }: any) => (
        <DateInput
          field="startDate"
          label={label}
          minDate="today"
          dateFormat="m/d/Y"
          altInput
          altFormat="m/d/Y"
          useIsoOnChange={false}
          value={value}
          onChange={(dateValue: string) =>
            handleInternationalWireStartDateChange(
              dateValue,
              onChange,
              setValue
            )
          }
          error={error?.message}
        />
      )}
    />
  )
);
InternationalWireStartDateField.displayName = "InternationalWireStartDateField";

type InternationalWireRecurrenceEndCardProps = {
  amount: number;
  control: any;
  count?: string;
  ends?: string;
  frequency?: string;
  startDate?: string;
  setValue: SetFormValue;
  untilDate?: string;
};

export const InternationalWireRecurrenceEndCard = ({
  amount,
  count,
  control,
  ends,
  frequency,
  startDate,
  setValue,
  untilDate,
}: InternationalWireRecurrenceEndCardProps) => {
  const { l10n } = useLocalization();
  const endsOnDate = ends === "on";
  const endsAfterCount = ends === "after";
  const defaultUntilDate = getDefaultRecurringUntilDate(startDate);
  const totalPayments = getInternationalWireTotalPayments(
    frequency,
    startDate,
    ends,
    untilDate || defaultUntilDate,
    count
  );

  return (
    <ContentCard kind="bordered">
      <h4 className="fontSize--m nds-sans margin--bottom--m">Ends</h4>
      <div className={styles.recurrenceRadioInput}>
        <Radio
          name="recurringRule.ends"
          checked={ends === "never"}
          onCheck={(value: string) =>
            handleInternationalWireRecurrenceEndChange(
              value,
              startDate,
              count,
              untilDate,
              setValue
            )
          }
          value="never"
        >
          {l10n.getString("ach-payment-never")}
        </Radio>
        <div className={styles.recurrenceEndDateInput}>
          <Radio
            name="recurringRule.ends"
            checked={endsOnDate}
            onCheck={(value: string) =>
              handleInternationalWireRecurrenceEndChange(
                value,
                startDate,
                count,
                untilDate,
                setValue
              )
            }
            value="on"
          >
            {l10n.getString("ach-payment-on")}{" "}
            <Controller
              name="recurringRule.untilDate"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }: any) => (
                <DateInput
                  disabled={!endsOnDate}
                  minDate={startDate}
                  dateFormat="m/d/Y"
                  altInput
                  altFormat="m/d/Y"
                  field="untilDate"
                  useIsoOnChange={false}
                  value={value || defaultUntilDate}
                  onChange={(dateValue: string) =>
                    onChange(formatInternationalWireStartDate(dateValue))
                  }
                  error={error?.message}
                />
              )}
            />
          </Radio>
        </div>
        <div className={styles.recurrenceCountInput}>
          <Radio
            name="recurringRule.ends"
            checked={endsAfterCount}
            onCheck={(value: string) =>
              handleInternationalWireRecurrenceEndChange(
                value,
                startDate,
                count,
                untilDate,
                setValue
              )
            }
            value="after"
          >
            {l10n.getString("ach-payment-after")}{" "}
            <Controller
              name="recurringRule.count"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }: any) => (
                <TextInput
                  disabled={!endsAfterCount}
                  field="count"
                  type="text"
                  value={
                    endsAfterCount ? value : value || DEFAULT_RECURRENCE_COUNT
                  }
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const countValue = event.target.value;
                    if (/^\d{0,2}$/.test(countValue)) onChange(countValue);
                  }}
                  error={error?.message}
                />
              )}
            />{" "}
            {l10n.getString("ach-payment-payments")}
          </Radio>
        </div>
      </div>
      {totalPayments > 0 && (
        <div className={styles.recurrenceTotalCount}>
          <Row>
            <Row.Item>{`${totalPayments} ${l10n.getString(
              "ach-payment-total-payments"
            )}:`}</Row.Item>
            <Row.Item shrink>
              {filters.currency(dollars(totalPayments * amount), {
                hasDecimal: true,
              })}
            </Row.Item>
          </Row>
        </div>
      )}
    </ContentCard>
  );
};
